import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { LessonProgressService, LessonProgressResponse } from '../services/lesson-progress/lesson-progress.service';

export interface LessonProgress {
  topicId: number;
  levelId: number;
  subTopicId?: number;
  fullTopic?: string;
  levelName?: string;
  topicName?: string;
  lastCompletedInteractionId: string | null;
  completedInteractions: string[];
  percentage: number;
  interactionStates?: Record<string, any>;
}

interface LessonPlayerState {
  // Map of lesson key (topicId-levelId-subTopicId) to progress
  progressMap: Record<string, LessonProgress>;
  
  // Current active lesson progress
  currentProgress: LessonProgress | null;

  // Sync state
  isSyncing: boolean;

  // Actions
  startLesson: (topicId: number, levelId: number, subTopicId?: number, fullTopic?: string, levelName?: string, topicName?: string) => void;
  updateProgress: (interactionId: string, percentage: number, interactionStates?: Record<string, any>) => void;
  clearProgress: (topicId: number, levelId: number, subTopicId?: number) => void;
  
  // Sync actions
  syncToDB: (lessonId: number, totalInteractions: number, interactionStates?: Record<string, any>) => Promise<void>;
  mergeServerProgress: (serverData: LessonProgressResponse, topicId?: number, levelId?: number, subTopicId?: number) => void;
  resetStore: () => void;
}

export const useLessonPlayerStore = create<LessonPlayerState>()(
  persist(
    (set, get) => ({
      progressMap: {},
      currentProgress: null,
      isSyncing: false,

      startLesson: (topicId, levelId, subTopicId, fullTopic, levelName, topicName) => {
        const key = `${topicId}-${levelId}-${subTopicId || 0}`;
        const existing = get().progressMap[key];
        
        if (existing) {
          const updated: LessonProgress = {
            ...existing,
            fullTopic: fullTopic || existing.fullTopic,
            levelName: levelName || existing.levelName,
            topicName: topicName || existing.topicName
          };
          set(state => ({
            progressMap: { ...state.progressMap, [key]: updated },
            currentProgress: updated
          }));
        } else {
          const newProgress: LessonProgress = {
            topicId,
            levelId,
            subTopicId,
            fullTopic,
            levelName,
            topicName,
            lastCompletedInteractionId: null,
            completedInteractions: [],
            percentage: 0,
            interactionStates: {}
          };
          set((state) => ({
            progressMap: { ...state.progressMap, [key]: newProgress },
            currentProgress: newProgress
          }));
        }
      },

      updateProgress: (interactionId, percentage, interactionStates) => {
        const current = get().currentProgress;
        if (!current) return;

        const key = `${current.topicId}-${current.levelId}-${current.subTopicId || 0}`;
        const updatedProgress: LessonProgress = {
          ...current,
          lastCompletedInteractionId: interactionId,
          completedInteractions: [...new Set([...current.completedInteractions, interactionId])],
          percentage,
          interactionStates: interactionStates || current.interactionStates
        };

        set((state) => ({
          progressMap: { ...state.progressMap, [key]: updatedProgress },
          currentProgress: updatedProgress
        }));
      },

      clearProgress: (topicId, levelId, subTopicId) => {
        const key = `${topicId}-${levelId}-${subTopicId || 0}`;
        set((state) => {
          const newMap = { ...state.progressMap };
          delete newMap[key];
          return { progressMap: newMap, currentProgress: null };
        });
      },

      syncToDB: async (lessonId, totalInteractions, interactionStates) => {
        const current = get().currentProgress;
        if (!current) return;

        set({ isSyncing: true });
        try {
          const service = new LessonProgressService();
          await service.upsertProgress({
            lessonId,
            status: current.percentage >= 100 ? 2 : 1,
            percentage: current.percentage,
            lastInteractionId: current.lastCompletedInteractionId,
            completedInteractionIds: current.completedInteractions,
            totalInteractions,
            interactionStates: interactionStates || current.interactionStates
          });
        } catch (e) {
          console.error('Progress sync failed', e);
        } finally {
          set({ isSyncing: false });
        }
      },

      mergeServerProgress: (serverData, overrideTopicId, overrideLevelId, overrideSubTopicId) => {
        const current = get().currentProgress;
        const topicId = overrideTopicId ?? serverData.topic_id ?? current?.topicId;
        const levelId = overrideLevelId ?? serverData.level_id ?? current?.levelId;
        const subTopicId = overrideSubTopicId ?? serverData.sub_topic_id ?? current?.subTopicId ?? 0;

        if (topicId === undefined || levelId === undefined) return;

        const key = `${topicId}-${levelId}-${subTopicId}`;
        const local = get().progressMap[key];

        // Server wins if it has more completed interactions
        if (!local || serverData.completed_interaction_ids.length > local.completedInteractions.length) {
          const merged: LessonProgress = {
            topicId,
            levelId,
            subTopicId,
            lastCompletedInteractionId: serverData.last_interaction_id,
            completedInteractions: serverData.completed_interaction_ids,
            percentage: serverData.percentage,
            interactionStates: serverData.interaction_states || {}
          };
          set(state => ({ 
            progressMap: { ...state.progressMap, [key]: merged }, 
            currentProgress: merged 
          }));
        }
      },
      resetStore: () => {
        set({ progressMap: {}, currentProgress: null, isSyncing: false });
      }
    }),
    {
      name: 'shoonit-lesson-player',
      storage: createJSONStorage(() => localStorage)
    }
  )
);

